import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const HEADING_FONT = 'Manrope, sans-serif';
const BODY_FONT = 'Inter, sans-serif';

/**
 * FAQ / Accordion Block Renderer
 * Variants: accordion, two_column, simple
 */
export default function FAQRenderer({ props, variant, isEditing }) {
  const {
    headline = 'Frequently Asked Questions',
    subheadline = '',
    items = [],
    background_color = '#FAFAF5',
  } = props;

  const headerEl = (
    <div className="text-center mb-12">
      <h2
        className="text-3xl sm:text-4xl font-bold text-stone-900 mb-3"
        style={{ fontFamily: HEADING_FONT }}
      >
        {headline}
      </h2>
      {subheadline && (
        <p
          className="text-lg text-stone-500 max-w-2xl mx-auto"
          style={{ fontFamily: BODY_FONT }}
        >
          {subheadline}
        </p>
      )}
    </div>
  );

  if (variant === 'accordion') {
    return (
      <section className="py-16 px-6" style={{ backgroundColor: background_color }}>
        <div className="max-w-3xl mx-auto">
          {headerEl}
          <AccordionItems items={items} isEditing={isEditing} />
        </div>
      </section>
    );
  }

  if (variant === 'two_column') {
    return (
      <section className="py-16 px-6" style={{ backgroundColor: background_color }}>
        <div className="max-w-5xl mx-auto">
          {headerEl}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {items.map((item, i) => (
              <div
                key={i}
                className={`p-6 rounded-xl ${i % 2 === 0 ? 'bg-white' : 'bg-stone-50'}`}
              >
                <h3
                  className="text-base font-semibold text-stone-900 mb-2"
                  style={{ fontFamily: HEADING_FONT }}
                >
                  {item.question}
                </h3>
                <p
                  className="text-sm text-stone-500 leading-relaxed"
                  style={{ fontFamily: BODY_FONT }}
                >
                  {item.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Simple variant — all expanded, stacked
  return (
    <section className="py-16 px-6" style={{ backgroundColor: background_color }}>
      <div className="max-w-3xl mx-auto">
        {headerEl}
        <div className="space-y-6">
          {items.map((item, i) => (
            <div key={i} className="border-b border-stone-200 pb-6 last:border-0">
              <h3
                className="text-base font-semibold text-stone-900 mb-2"
                style={{ fontFamily: HEADING_FONT }}
              >
                {item.question}
              </h3>
              <p
                className="text-sm text-stone-500 leading-relaxed"
                style={{ fontFamily: BODY_FONT }}
              >
                {item.answer}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AccordionItems({ items, isEditing }) {
  // In edit mode, show first 2 expanded by default
  const [openIndex, setOpenIndex] = useState(isEditing ? 0 : -1);

  const isOpen = (i) => {
    if (isEditing && i < 2) return true;
    return openIndex === i;
  };

  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div
          key={i}
          className="border border-stone-200 rounded-xl overflow-hidden bg-white"
        >
          <button
            onClick={() => setOpenIndex(openIndex === i ? -1 : i)}
            className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-stone-50 transition-colors"
          >
            <span
              className="text-base font-semibold text-stone-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {item.question}
            </span>
            <ChevronDown
              className={`w-5 h-5 text-stone-400 transition-transform flex-shrink-0 ml-4 ${
                isOpen(i) ? 'rotate-180' : ''
              }`}
            />
          </button>
          {isOpen(i) && (
            <div className="px-6 pb-4">
              <p
                className="text-sm text-stone-500 leading-relaxed"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {item.answer}
              </p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
